/**
 * Phase 17 — the gateway ranking as stored (gateway satellite
 * `orderFormulaJson`, inherited with the gateway strategy section) and its
 * pure rules, shared by the planner, the RANDOM draw at generation and the
 * editor (so the shares on screen are the shares the engine uses):
 * - ORDER by formula: ranked gateways best first, then the unranked ones in
 *   the order the person dragged them.
 * - WEIGHTS by formula: each ranked gateway's share of the positive scores; an
 *   unranked gateway gets the mean ranked share so it keeps learning; no
 *   positive score at all → equal shares.
 * For Media Devoted | Created By Ciro Autiero
 */
import { notEnoughDataRule, rulesWithLegacyFloor } from "../rules/default-rules";
import { routeTestTexts } from "../text-ranking/policy/route-test-texts";
import { parseRules } from "../rules/parse-rules";
import { DROP_GEN_GATEWAY_RULE_ACTIONS, DROP_GEN_TAGS_CHECK } from "../rules/rules.types";
import { defaultHourFormula } from "../hour-formula";
import { routeTestMetricIds } from "../route-test/route-test-metrics";
import { routeTestSource } from "../route-test/route-test";
import type { DropGenGatewayRanking, DropGenRankedRoute } from "./gateway-ranking.types";

/** The old "Min volume / gateway" default — the default exclude rule's number. */
export const DEFAULT_GATEWAY_FLOOR = 100;

/** Ranked on CR over the last 30 days; "Not enough data" under 100 unique visits. */
export function defaultGatewayRanking(): DropGenGatewayRanking {
  const formula = defaultHourFormula("last-30-days", "cr");
  const id = formula.groups[0].id;
  formula.groups[0] = {
    ...formula.groups[0],
    id: "gateways-1",
    name: "Gateways",
  };
  formula.groupWeights = { "gateways-1": formula.groupWeights[id] ?? 100 };
  return { formula, rules: [notEnoughDataRule(DEFAULT_GATEWAY_FLOOR)] };
}

/** Stored JSON → a usable config; anything malformed falls back piece by piece. */
export function parseGatewayRanking(json: unknown): DropGenGatewayRanking {
  const base = defaultGatewayRanking();
  if (typeof json !== "object" || json === null) return base;
  const r = json as Record<string, unknown>;
  const f = r.formula as DropGenGatewayRanking["formula"] | undefined;
  const formula =
    f && Array.isArray(f.groups) && f.groups.length > 0 ? { groups: f.groups, groupWeights: f.groupWeights ?? {} } : base.formula;
  return {
    formula,
    rules: parseRules(rulesWithLegacyFloor(r, "minVolume", DEFAULT_GATEWAY_FLOOR), DROP_GEN_GATEWAY_RULE_ACTIONS, {
      ...DROP_GEN_TAGS_CHECK,
      metrics: routeTestMetricIds(routeTestSource(r)),
    }),
    ...keptSource(r.source),
    ...(typeof r.routeTestSettleMin === "number" &&
    Number.isInteger(r.routeTestSettleMin) &&
    r.routeTestSettleMin >= 0 &&
    r.routeTestSettleMin <= 1440
      ? { routeTestSettleMin: r.routeTestSettleMin }
      : {}),
    ...(typeof r.routeTestTexts === "object" && r.routeTestTexts !== null ? { routeTestTexts: routeTestTexts(r.routeTestTexts) } : {}),
  };
}

function keptSource(value: unknown): { source?: "drops" | "gateway" } {
  return value === "drops" || value === "gateway" ? { source: value } : {};
}

type Ranked = Pick<DropGenRankedRoute, "routeId" | "score" | "unranked">;

const rankedScore = (byRoute: Map<number, Ranked>, id: number): number | null => {
  const r = byRoute.get(id);
  return r && !r.unranked && r.score !== null ? r.score : null;
};

/** The pool's route ids in formula order: ranked best first, unranked as dragged. */
export function gatewayFormulaOrder(poolOrder: number[], ranked: Ranked[]): number[] {
  const byRoute = new Map(ranked.map((r) => [r.routeId, r]));
  const scored = poolOrder.filter((id) => rankedScore(byRoute, id) !== null);
  scored.sort((a, b) => rankedScore(byRoute, b)! - rankedScore(byRoute, a)!);
  return [...scored, ...poolOrder.filter((id) => rankedScore(byRoute, id) === null)];
}

/** Route id → share in percent (sums to 100) — the formula's weights. */
export function gatewayFormulaWeights(poolOrder: number[], ranked: Ranked[]): Map<number, number> {
  const byRoute = new Map(ranked.map((r) => [r.routeId, r]));
  const positive = poolOrder.map((id) => rankedScore(byRoute, id)).filter((s): s is number => s !== null && s > 0);
  if (positive.length === 0) return new Map(poolOrder.map((id) => [id, 100 / Math.max(1, poolOrder.length)]));
  const mean = positive.reduce((s, v) => s + v, 0) / positive.length;
  const raw = poolOrder.map((id): [number, number] => {
    const score = rankedScore(byRoute, id);
    return [id, score === null ? mean : Math.max(0, score)];
  });
  const total = raw.reduce((s, [, v]) => s + v, 0);
  return new Map(raw.map(([id, v]) => [id, (v / total) * 100]));
}

export interface DropGenGatewayRuleOutcome {
  /** Routes a "leave the pool" rule fired on, with that rule's name and words. */
  left: { routeId: number; name: string; reason: string }[];
  /** Routes a "last resort" rule fired on: only when every other route is full. */
  lastResort: number[];
}

/** Phase 19 — what the gateway rules did, from the ranking's per-route hits. */
export function gatewayRuleOutcome(routes: Pick<DropGenRankedRoute, "routeId" | "ruleHits">[]): DropGenGatewayRuleOutcome {
  const firedOn = (r: (typeof routes)[number], action: string) =>
    (r.ruleHits ?? []).find((h) => h.state === "fired" && h.action === action);
  return {
    left: routes.flatMap((r) => {
      const h = firedOn(r, "leave_pool");
      return h ? [{ routeId: r.routeId, name: h.name, reason: h.reason }] : [];
    }),
    lastResort: routes.filter((r) => firedOn(r, "last_resort")).map((r) => r.routeId),
  };
}
